<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button text="" default-href="/useful-links"></ion-back-button>
        </ion-buttons>
        <ion-title>Полезные ссылки (админ)</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="openCreate()">
            <ion-icon :icon="addOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true" @ionScroll="onContentScroll">
      <div class="ion-padding">
        <p v-if="loading" class="ion-text-center">Загрузка...</p>
        <ion-list v-else lines="full">
          <ion-item v-for="row in items" :key="row.id" class="link-item">
            <ion-label>
              <h2>{{ row.title }}</h2>
              <p>
                <span v-if="row.visibleToAll">Для всех</span>
                <span v-else>Пользователей: {{ row.userIds?.length ?? 0 }}</span>
                · порядок {{ row.sortOrder }}
                <span v-if="!row.active" class="badge-inactive">выкл</span>
              </p>
            </ion-label>
            <ion-button fill="clear" slot="end" class="link-row-edit-btn" @click="openEdit(row)">
              <ion-icon :icon="createOutline"></ion-icon>
            </ion-button>
            <ion-button fill="clear" slot="end" color="danger" @click="confirmDelete(row)">
              <ion-icon :icon="trashOutline"></ion-icon>
            </ion-button>
          </ion-item>
        </ion-list>
      </div>

      <ion-modal :is-open="modalOpen" @didDismiss="closeModal" class="useful-links-editor-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>{{ editingId ? 'Редактирование' : 'Новая ссылка' }}</ion-title>
          </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding useful-links-modal-content">
          <ion-item class="field">
            <ion-label position="stacked">Заголовок</ion-label>
            <ion-input v-model="form.title" placeholder="Название"></ion-input>
          </ion-item>
          <ion-item class="field">
            <ion-label position="stacked">Целевой URL</ion-label>
            <ion-input v-model="form.targetUrl" placeholder="https://…"></ion-input>
          </ion-item>
          <ion-item class="field">
            <ion-label>Видна всем авторизованным</ion-label>
            <ion-toggle v-model="form.visibleToAll"></ion-toggle>
          </ion-item>

          <div v-if="!form.visibleToAll" class="user-access-block">
            <p class="user-access-hint">Доступ отдельным пользователям</p>
            <p class="user-access-count">Выбрано: {{ (form.userIds || []).length }}</p>
            <ion-searchbar
              v-model="userPickerFilterQuery"
              placeholder="Поиск по имени, фамилии или логину"
              :debounce="200"
              class="admin-add-users-search"
            ></ion-searchbar>
            <ion-list v-if="users.length" lines="full" class="user-pick-list">
              <ion-item v-for="u in filteredUsersForAccess" :key="String(u.id)" class="user-pick-item">
                <ion-checkbox
                  slot="start"
                  :checked="isUserSelected(u.id)"
                  @ionChange="onUserAccessChange(u, $event)"
                ></ion-checkbox>
                <ion-label>
                  <h3>{{ userDisplayName(u) }}</h3>
                  <p class="user-pick-login">{{ u.login }}</p>
                </ion-label>
              </ion-item>
            </ion-list>
            <p v-else class="modal-hint">Загрузите список пользователей (обновите страницу).</p>
            <p v-if="users.length && !filteredUsersForAccess.length" class="modal-hint">Ничего не найдено.</p>
          </div>

          <ion-item class="field">
            <ion-label position="stacked">Порядок сортировки</ion-label>
            <ion-input type="number" v-model.number="form.sortOrder"></ion-input>
          </ion-item>
          <ion-item class="field">
            <ion-label>Активна</ion-label>
            <ion-toggle v-model="form.active"></ion-toggle>
          </ion-item>

          <div class="admin-modal-actions">
            <ion-button expand="block" class="admin-primary-btn" @click="save" :disabled="saving">
              {{ saving ? 'Сохранение…' : 'Сохранить' }}
            </ion-button>
            <ion-button expand="block" fill="outline" class="admin-outline-btn" @click="closeModal">Закрыть</ion-button>
          </div>
        </ion-content>
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSearchbar,
  IonTitle,
  IonToggle,
  IonToolbar,
  alertController
} from '@ionic/vue';
import { addOutline, createOutline, trashOutline } from 'ionicons/icons';
import { defineComponent } from 'vue';
import usefulLinksService, { IUsefulLinkAdmin } from '@/services/useful-links.service';
import AdminService from '@/services/admin.service';
import { User } from '@/model/user.model';

const emptyForm = (): IUsefulLinkAdmin => ({
  title: '',
  targetUrl: '',
  visibleToAll: true,
  sortOrder: 0,
  active: true,
  userIds: []
});

export default defineComponent({
  name: 'AdminUsefulLinks',
  components: {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCheckbox,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonSearchbar,
    IonTitle,
    IonToggle,
    IonToolbar
  },
  data() {
    return {
      items: [] as IUsefulLinkAdmin[],
      users: [] as User[],
      loading: true,
      headerScrolled: false,
      modalOpen: false,
      editingId: null as number | null,
      form: emptyForm(),
      saving: false,
      addOutline,
      createOutline,
      trashOutline,
      adminService: new AdminService(),
      userPickerFilterQuery: ''
    };
  },
  computed: {
    filteredUsersForAccess(): User[] {
      const q = (this.userPickerFilterQuery || '').trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter((u) => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const login = (u.login || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(q) || login.includes(q) || email.includes(q);
      });
    }
  },
  async mounted() {
    await this.loadUsers();
    await this.load();
  },
  ionViewWillEnter() {
    this.load();
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    async loadUsers() {
      try {
        this.users = await this.adminService.getAdminUsersPage(0, 2000);
      } catch {
        this.users = [];
      }
    },
    async load() {
      this.loading = true;
      try {
        this.items = await usefulLinksService.adminList();
      } catch {
        this.items = [];
      }
      this.loading = false;
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.userPickerFilterQuery = '';
      this.modalOpen = true;
    },
    openEdit(row: IUsefulLinkAdmin) {
      this.editingId = row.id ?? null;
      this.form = {
        title: row.title,
        targetUrl: row.targetUrl,
        visibleToAll: !!row.visibleToAll,
        sortOrder: row.sortOrder ?? 0,
        active: row.active !== false,
        userIds: [...(row.userIds || [])]
      };
      this.userPickerFilterQuery = '';
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
      this.userPickerFilterQuery = '';
    },
    userDisplayName(u: User): string {
      const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
      return n || u.login || String(u.id ?? '');
    },
    isUserSelected(id: unknown): boolean {
      const n = Number(id);
      if (Number.isNaN(n)) return false;
      return (this.form.userIds || []).some((x) => Number(x) === n);
    },
    onUserAccessChange(u: User, ev: CustomEvent) {
      const checked = !!(ev as CustomEvent<{ checked: boolean }>).detail?.checked;
      const id = Number(u.id);
      if (Number.isNaN(id)) return;
      const ids = [...(this.form.userIds || [])];
      const idx = ids.findIndex((x) => Number(x) === id);
      if (checked && idx < 0) ids.push(id);
      if (!checked && idx >= 0) ids.splice(idx, 1);
      this.form.userIds = ids;
    },
    async save() {
      const payload: IUsefulLinkAdmin = {
        ...this.form,
        sortOrder: Number(this.form.sortOrder) || 0,
        userIds: this.form.visibleToAll ? [] : [...(this.form.userIds || [])]
      };
      this.saving = true;
      try {
        if (this.editingId != null) {
          await usefulLinksService.adminUpdate(this.editingId, { ...payload, id: this.editingId });
        } else {
          await usefulLinksService.adminCreate(payload);
        }
        this.modalOpen = false;
        await this.load();
      } catch (e) {
        console.error(e);
        const a = await alertController.create({
          header: 'Ошибка',
          message: 'Не удалось сохранить',
          buttons: ['OK']
        });
        await a.present();
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(row: IUsefulLinkAdmin) {
      const a = await alertController.create({
        header: 'Удалить ссылку?',
        message: row.title,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              if (row.id == null) return;
              try {
                await usefulLinksService.adminDelete(row.id);
                await this.load();
              } catch (e) {
                console.error(e);
              }
            }
          }
        ]
      });
      await a.present();
    }
  }
});
</script>

<style scoped>
.link-item {
  --background: rgba(42, 42, 42, 0.6);
  --color: #fff;
  --border-color: rgba(255, 255, 255, 0.1);
}
.link-item .link-row-edit-btn {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  --color-hover: var(--ion-color-orange-tint);
  --color-focused: var(--ion-color-orange-tint);
}
.link-item .link-row-edit-btn ion-icon {
  color: var(--ion-color-orange);
}
ion-item h2,
ion-item p {
  color: #fff;
}
.field {
  --background: rgba(42, 42, 42, 0.4);
  --color: #fff;
}
.badge-inactive {
  color: #ffc107;
  margin-left: 6px;
}

.user-access-block {
  margin: 12px 0;
}
.user-access-hint {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  margin: 0 0 4px;
  font-weight: 600;
}
.user-access-count {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  margin: 0 0 8px;
}
.modal-hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.9rem;
  padding: 8px 0;
}

/* Кнопки в форме — как в админке тестов / разделов */
.admin-modal-actions {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.admin-modal-actions .admin-primary-btn,
.admin-modal-actions .admin-outline-btn {
  margin-top: 0;
}

.admin-primary-btn,
.admin-primary-btn.ion-activated,
.admin-primary-btn.ion-focused {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --background-focused: var(--ion-color-orange-tint);
  --color: #fff;
  font-weight: 600;
  --border-radius: 14px;
}

.admin-outline-btn,
.admin-outline-btn.ion-activated,
.admin-outline-btn.ion-focused {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
  --border-radius: 14px;
}

.user-pick-list {
  max-height: min(45vh, 320px);
  overflow-y: auto;
  background: transparent;
}
.user-pick-item {
  --background: rgba(255, 255, 255, 0.06);
  --border-color: rgba(255, 255, 255, 0.08);
  margin-bottom: 6px;
  border-radius: 8px;
}
.user-pick-item ion-label h3 {
  color: #fff;
  font-size: 0.95rem;
}
.user-pick-login {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
}

.useful-links-modal-content ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: var(--ion-color-orange);
  --border-color: rgba(255, 255, 255, 0.5);
  --border-color-checked: var(--ion-color-orange);
  --checkmark-color: #fff;
  margin-right: 8px;
}
</style>

<!-- Модалка рендерится в overlay — глобальные стили для оранжевых кнопок и списка -->
<style>
.useful-links-editor-modal .admin-primary-btn,
.useful-links-editor-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: #fff !important;
  font-weight: 600;
}
.useful-links-editor-modal .admin-outline-btn,
.useful-links-editor-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.useful-links-editor-modal ion-list.user-pick-list,
.useful-links-editor-modal .user-pick-item {
  --background: transparent !important;
}
.useful-links-editor-modal .user-pick-item::part(native) {
  --background: rgba(255, 255, 255, 0.06) !important;
}
.useful-links-editor-modal ion-checkbox {
  --checkbox-background-checked: var(--ion-color-orange) !important;
  --border-color-checked: var(--ion-color-orange) !important;
  --checkmark-color: #fff !important;
}
</style>
